import React, { useEffect, useState } from "react";

import tailwindBase from "~/css/base.css?url";
import semantic from "~/css/semantic.css?url";
import legacySemantic from "~/css/semantic/legacy-semantic.css?url";
import textVariablesCompat from "~/css/text-variables-compat.css?url";
import textVariables from "~/css/text-variables.css?url";
import legacyTextStyles from "~/ui/legacy-text-styles.css?url";

import StylesheetsContext from "./StylesheetsContext";

function useToggleStylesheet(url: string, enabled: boolean) {
	useEffect(() => {
		const link = document.querySelector<HTMLLinkElement>(`link[href="${url}"]`);

		if (!link) {
			console.warn(`Stylesheet link not found for url: ${url}`);
			return;
		}

		link.disabled = !enabled;
	}, [url, enabled]);
}

export function StylesheetsProvider({ children }: { children: React.ReactNode }) {
	const [isUXSimplificationEnabled, setUXSimplificationStylesEnabled] = useState(
		document.documentElement.dataset.uxSimplification === "enabled",
	);

	// New stylesheets
	useToggleStylesheet(semantic, isUXSimplificationEnabled);
	useToggleStylesheet(textVariables, isUXSimplificationEnabled);
	useToggleStylesheet(tailwindBase, isUXSimplificationEnabled);

	// Old stylesheets
	useToggleStylesheet(legacySemantic, !isUXSimplificationEnabled);
	// TODO: The legacy text styles will be necessary until we've completely removed all references to variables and fonts in it.
	useToggleStylesheet(legacyTextStyles, true);
	useToggleStylesheet(textVariablesCompat, !isUXSimplificationEnabled);

	useEffect(() => {
		document.documentElement.dataset.uxSimplification = isUXSimplificationEnabled ? "enabled" : "disabled";
	}, [isUXSimplificationEnabled]);

	// Unrelated to the new UX — there are cases where the theme isn't set (i.e. the error page)
	// This is a fallback to system preference.
	useEffect(() => {
		if (
			document.documentElement.dataset.theme === "theme-dark" ||
			document.documentElement.dataset.theme === "theme-light"
		) {
			return;
		}

		const matchDarkMode = window.matchMedia("(prefers-color-scheme: dark)");
		const isDark = matchDarkMode.matches;
		if (isDark) {
			document.documentElement.dataset.theme = "theme-dark";
		} else {
			document.documentElement.dataset.theme = "theme-light";
		}
	}, []);

	return <StylesheetsContext.Provider value={setUXSimplificationStylesEnabled}>{children}</StylesheetsContext.Provider>;
}
